import { useEffect, useState } from 'react';
import type { TransactionListItem, TransactionPropertyTotal } from '@pet/shared';
import { getTransactions, ReceiptApiError } from '../lib/transactionReadClient';
import { formatCentsAsCurrency } from '../lib/money';
import { formatIsoDate } from '../lib/displayDate';
import { useListScroll } from '../lib/listScroll';
import {
  DEFAULT_EXPENSE_FILTERS,
  DEFAULT_SORT,
  SORT_OPTIONS,
  chipOptions,
  expenseActiveParts,
  expenseChoices,
  filterExpenses,
  fromChip,
  hasActiveFilter,
  isDateSort,
  isHouseOnly,
  monthGroups,
  sortExpenses,
  toChip,
  type ExpenseFilters,
  type SortKey,
} from '../lib/readFilters';
import TopBar from '../components/TopBar';
import FilterPanel, { FilterStatus, NoMatch } from '../components/FilterPanel';
import PaymentDetail from './PaymentDetail';
import type { FixDraft } from '../lib/fixDraft';
import styles from './Payments.module.css';

interface ExpensesProps {
  onDone: () => void;
  // "Fix this payment" (AD-19), passed through to the detail.
  onFix?: (draft: FixDraft, resetIngestionId: string | null) => void;
}

type Screen =
  | { name: 'loading' }
  | {
      name: 'ready';
      transactions: TransactionListItem[];
      propertyTotals: TransactionPropertyTotal[];
    }
  | { name: 'error'; message: string };

const NOUNS = { one: 'expense', other: 'expenses' };

// Local HTTP-status -> message mapping, matching the Payments.tsx precedent.
// A 401 never logs the user out (AD-06) — it shows a retryable message.
function mapGetError(err: unknown): string {
  if (err instanceof ReceiptApiError && err.status === 401) {
    return "Your session couldn't be confirmed. Please try again.";
  }
  return "We couldn't load your expenses. Please try again.";
}

// Plain language for where an expense came from. A row with no payment was logged
// through Add Expense (the receipt flow) and is not part of any payment — say so in
// words. Never render schema terms here ("paymentId", "null", "parentless").
// A row that is part of a payment opens it; its cue says so.
const FROM_RECEIPT_NOTE = 'Logged from a receipt — not part of a payment';
const OPEN_PAYMENT_CUE = 'Tap to see the payment';

export default function Expenses({ onDone, onFix }: ExpensesProps) {
  const [screen, setScreen] = useState<Screen>({ name: 'loading' });
  const [selectedPaymentId, setSelectedPaymentId] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  // Filters and sort live here, so they survive a trip to a payment and back (V7);
  // Home unmounts this screen and resets them.
  const [filters, setFilters] = useState<ExpenseFilters>(DEFAULT_EXPENSE_FILTERS);
  const [sort, setSort] = useState<SortKey>(DEFAULT_SORT);
  const [panelOpen, setPanelOpen] = useState(false);
  const rememberScroll = useListScroll(selectedPaymentId);

  useEffect(() => {
    let cancelled = false;
    // A reload after a delete keeps the list shown until the refetch resolves, so the scroll
    // position comes back (useListScroll restores against the list, not a loading page).
    setScreen((s) => (s.name === 'ready' ? s : { name: 'loading' }));
    getTransactions()
      .then(({ transactions, propertyTotals }) => {
        if (!cancelled) setScreen({ name: 'ready', transactions, propertyTotals });
      })
      .catch((err) => {
        if (!cancelled) setScreen({ name: 'error', message: mapGetError(err) });
      });
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  // The panel and the list each open at their top.
  function openPanel() {
    setPanelOpen(true);
    window.scrollTo(0, 0);
  }
  function closePanel() {
    setPanelOpen(false);
    window.scrollTo(0, 0);
  }
  function clearAll() {
    setFilters(DEFAULT_EXPENSE_FILTERS);
    setSort(DEFAULT_SORT);
  }
  function setFilter(key: keyof ExpenseFilters) {
    return (chip: string) => setFilters((f) => ({ ...f, [key]: fromChip(chip) }));
  }

  // Hosted exactly as Payments hosts it: this component stays mounted, so the fetched
  // list survives and is not fetched again on return.
  if (selectedPaymentId !== null) {
    return (
      <PaymentDetail
        paymentId={selectedPaymentId}
        backLabel="Back to expenses"
        onBack={() => setSelectedPaymentId(null)}
        onFix={onFix}
        onDeleted={() => {
          setSelectedPaymentId(null);
          setReloadKey((k) => k + 1);
        }}
      />
    );
  }

  const transactions = screen.name === 'ready' ? screen.transactions : [];
  const choices = expenseChoices(transactions);
  const shown = sortExpenses(filterExpenses(transactions, filters), sort);

  if (panelOpen && screen.name === 'ready') {
    return (
      <FilterPanel
        sections={[
          { heading: 'Sort', options: SORT_OPTIONS, selected: sort, onSelect: (v) => setSort(v as SortKey) },
          { heading: 'House', options: chipOptions(choices.houses), selected: toChip(filters.house), onSelect: setFilter('house') },
          { heading: 'Category', options: chipOptions(choices.categories), selected: toChip(filters.category), onSelect: setFilter('category') },
          { heading: 'Year', options: chipOptions(choices.years), selected: toChip(filters.year), onSelect: setFilter('year') },
          { heading: 'Paid to', options: chipOptions(choices.payees), selected: toChip(filters.paidTo), onSelect: setFilter('paidTo') },
        ]}
        shownCount={shown.length}
        nouns={NOUNS}
        onClearAll={clearAll}
        onClose={closePanel}
      />
    );
  }

  // With no rows there is nothing to filter: no action, no panel.
  const topBar = (
    <TopBar
      title="Expenses"
      backLabel="Home"
      onBack={onDone}
      action={transactions.length > 0 ? { label: 'Filter & sort', onClick: openPanel } : undefined}
    />
  );

  if (screen.name === 'loading') {
    return (
      <main className={styles.container}>
        {topBar}
        <p className={styles.statusText} aria-busy="true">
          Loading expenses…
        </p>
      </main>
    );
  }

  if (screen.name === 'error') {
    return (
      <main className={styles.container}>
        {topBar}
        <p className={styles.banner} role="alert">
          {screen.message}
        </p>
        <button className={styles.primaryButton} onClick={() => setReloadKey((k) => k + 1)}>
          Try again
        </button>
      </main>
    );
  }

  const { propertyTotals } = screen;
  // Both figures come from the server's per-property groupBy over the whole ledger
  // (AD-17) — never from the rows on this page, which are capped.
  const grandTotalCents = propertyTotals.reduce((sum, t) => sum + t.totalCents, 0);
  const expenseCount = propertyTotals.reduce((sum, t) => sum + t.transactionCount, 0);

  // V5: with no filter, the whole ledger's totals; with House alone, that house's server
  // total and count; with any other filter, no dollar figure at all — a total is never
  // computed from the loaded rows.
  const renderTotals = (count: number, totalCents: number, byHouse: TransactionPropertyTotal[]) => (
    <>
      <div className={styles.summaryTotal}>
        <span>
          {count} {count === 1 ? 'expense' : 'expenses'}
        </span>
        <span>{formatCentsAsCurrency(totalCents)}</span>
      </div>

      <h3 className={styles.sectionHeading}>By house</h3>
      <div className={styles.summaryList}>
        {byHouse.map((t) => (
          <div key={t.propertyId} className={styles.summaryRow}>
            <span className={styles.summaryLabel}>{t.propertyAddress}</span>
            <span className={styles.summaryValue}>{formatCentsAsCurrency(t.totalCents)}</span>
          </div>
        ))}
      </div>
    </>
  );

  let totals: JSX.Element | null;
  if (!hasActiveFilter(filters)) {
    totals = renderTotals(expenseCount, grandTotalCents, propertyTotals);
  } else if (isHouseOnly(filters)) {
    // Chips come from loaded rows, all of which the server groups, so the house is always
    // here; if it ever were not, no totals show rather than a wrong one.
    const house = propertyTotals.find((t) => t.propertyId === filters.house);
    totals = house ? renderTotals(house.transactionCount, house.totalCents, [house]) : null;
  } else {
    totals = <p className={styles.totalsNote}>Totals show when only a house is chosen.</p>;
  }

  const renderCard = (t: TransactionListItem) => {
    const content = (
      <>
        <span className={styles.cardTitleRow}>
          <span className={styles.cardPayee}>{t.vendorOrPayer}</span>
          <span className={styles.cardAmount}>{formatCentsAsCurrency(t.amountCents)}</span>
        </span>
        <span className={styles.cardDetail}>{formatIsoDate(t.date)}</span>
        <span className={styles.cardDetail}>{t.propertyAddress}</span>
        <span className={styles.cardDetail}>Category: {t.taxCategoryLabel}</span>
        {/* description is nullable; a null renders nothing, never the word "null". */}
        {t.description && <span className={styles.cardDetail}>{t.description}</span>}
      </>
    );
    const { paymentId } = t;
    if (paymentId === null) {
      return (
        <li key={t.id} className={styles.lineCard}>
          {content}
          <span className={styles.receiptNote}>{FROM_RECEIPT_NOTE}</span>
        </li>
      );
    }
    return (
      <li key={t.id} className={styles.cardItem}>
        <button
          type="button"
          className={styles.paymentCard}
          onClick={() => {
            rememberScroll();
            setSelectedPaymentId(paymentId);
          }}
          aria-label={`${t.vendorOrPayer}, ${formatCentsAsCurrency(t.amountCents)}, ${formatIsoDate(t.date)} — see the payment`}
        >
          {content}
          <span className={styles.cardCue}>{OPEN_PAYMENT_CUE}</span>
        </button>
      </li>
    );
  };

  return (
    <main className={styles.container}>
      {topBar}

      {transactions.length === 0 ? (
        <p className={styles.emptyNote}>No expenses recorded yet.</p>
      ) : shown.length === 0 ? (
        <NoMatch nouns={NOUNS} onClearAll={clearAll} />
      ) : (
        <>
          {/* M is the server's count over the whole ledger, never the loaded rows. */}
          <FilterStatus
            shown={shown.length}
            total={expenseCount}
            nouns={NOUNS}
            activeParts={expenseActiveParts(filters, sort, choices)}
            onClearAll={clearAll}
          />
          {totals}

          <h3 className={styles.sectionHeading}>Every expense</h3>
          {isDateSort(sort) ? (
            monthGroups(shown).map((g) => (
              <section key={g.key} className={styles.monthSection}>
                <h2 className={styles.monthHeading}>{g.label}</h2>
                <ul className={styles.cardList} aria-label={`Expenses, ${g.label}`}>
                  {g.rows.map(renderCard)}
                </ul>
              </section>
            ))
          ) : (
            <ul className={styles.cardList} aria-label="Expenses">
              {shown.map(renderCard)}
            </ul>
          )}
          <div className={styles.navRow}>
            <button className={styles.secondaryButton} onClick={() => window.scrollTo(0, 0)}>
              Back to top
            </button>
          </div>
        </>
      )}
    </main>
  );
}
